/**
 * Payment API Service
 * Handles Q.AI token purchase via NOWPayments (USDT TRC20)
 *
 * Routing: Frontend → FIX Engine (proxy) → DataFeed → NOWPayments
 * Base URL: baseURL from config (FIX Engine at localhost:7261)
 *
 * Frontend Routes (FIX Engine Proxy - JWT-based user identification):
 * - GET  /api/q/payments/packages     - List available token packages (public)
 * - POST /api/q/payments/create       - Create a new payment (JWT required)
 * - GET  /api/q/payments/{id}/status  - Check payment status (JWT required)
 * - GET  /api/q/payments/history      - User's payment history (JWT required)
 *
 * FIX Engine transforms to DataFeed routes (adds userId from JWT):
 * - GET  /api/q/payments/packages           → same (public)
 * - POST /api/q/payments/create             → /api/q/payments/create/{userId}
 * - GET  /api/q/payments/{id}/status        → /api/q/payments/{id}/status/{userId}
 * - GET  /api/q/payments/history            → /api/q/payments/history/{userId}
 *
 * Security: User ID extracted from JWT server-side (prevents manipulation)
 *
 * IMPORTANT: All responses use snake_case (payment_id, invoice_url, etc.)
 */

import axios from 'axios';
import { baseURL } from '../config';

// Payment API routes through FIX Engine proxy (extracts userId from JWT)
const PAYMENT_API_BASE = `${baseURL}/api/q/payments`;

// ═══════════════════════════════════════════════════════════════════════════
// HELPER - Get auth headers
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Get authorization headers for authenticated endpoints
 * Uses qai_token (Q.AI standalone) or token (main app) from localStorage
 */
const getAuthHeaders = () => {
    const token = localStorage.getItem('qai_token') || localStorage.getItem('token');
    return {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
    };
};

// ═══════════════════════════════════════════════════════════════════════════
// GET /packages - List Available Packages (Public)
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Get available token packages
 * No authentication required - can be shown before login
 *
 * @returns {Promise<{success: boolean, data?: {packages: Package[]}, error?: string}>}
 *
 * Response format:
 * {
 *   packages: [
 *     { id, name, description, tokens, bonusTokens, totalTokens, priceUsd, isFeatured }
 *   ]
 * }
 */
export const getPackages = async () => {
    try {
        const response = await axios.get(`${PAYMENT_API_BASE}/packages`);

        return {
            success: true,
            data: response.data
        };
    } catch (error) {
        console.error('[paymentApi] Failed to get packages:', error);
        return {
            success: false,
            error: error.response?.data?.error || error.message || 'Failed to load packages'
        };
    }
};

// ═══════════════════════════════════════════════════════════════════════════
// POST /create - Create Payment (JWT Required)
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Create a new payment for a token package
 * Returns invoice_url to redirect user to NOWPayments
 *
 * @param {number} packageId - Package ID to purchase
 * @returns {Promise<{success: boolean, data?: CreatePaymentResponse, error?: string}>}
 *
 * Response format (snake_case):
 * {
 *   payment_id: 123,
 *   invoice_url: "https://nowpayments.io/payment/?iid=...",
 *   price_amount: 35.00,
 *   price_currency: "usd",
 *   tokens: 220,
 *   expires_at: "2025-12-23T15:30:00Z"
 * }
 *
 * Error scenarios:
 * - "Maximum pending payments (3) reached" - User has too many unpaid invoices
 * - "Invalid package selected" - Package ID doesn't exist
 * - "User not authenticated" - JWT token missing/invalid
 * - 429 Too Many Requests - Rate limit exceeded (5 req/min)
 */
export const createPayment = async (packageId) => {
    try {
        if (!packageId) {
            return {
                success: false,
                error: 'Package ID is required'
            };
        }

        const response = await axios.post(
            `${PAYMENT_API_BASE}/create`,
            { packageId },
            { headers: getAuthHeaders() }
        );

        console.log('[paymentApi] Payment created:', {
            payment_id: response.data.payment_id,
            tokens: response.data.tokens
        });

        return {
            success: true,
            data: response.data
        };
    } catch (error) {
        console.error('[paymentApi] Failed to create payment:', error);

        // Handle specific error cases
        const errorMessage = error.response?.data?.error
            || error.response?.data?.message
            || error.message
            || 'Failed to create payment';

        // Handle rate limiting
        if (error.response?.status === 429) {
            return {
                success: false,
                error: 'Too many requests. Please wait a moment and try again.'
            };
        }

        return {
            success: false,
            error: errorMessage
        };
    }
};

// ═══════════════════════════════════════════════════════════════════════════
// GET /{id}/status - Check Payment Status (JWT Required)
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Check payment status
 * Used on success page to poll for payment confirmation
 *
 * @param {number} paymentId - Payment ID to check
 * @returns {Promise<{success: boolean, data?: PaymentStatus, error?: string}>}
 *
 * Response format (snake_case):
 * {
 *   id: 123,
 *   status: "processing",
 *   tokens_credited: false,
 *   price_amount: 35.00,
 *   price_currency: "usd",
 *   created_at: "2025-12-23T15:00:00Z",
 *   paid_at: null
 * }
 *
 * Status values:
 * - "pending"     - Payment created, waiting for user
 * - "processing"  - Payment detected, confirming
 * - "accepted"    - Payment complete, tokens credited
 * - "rejected"    - Payment failed
 * - "expired"     - Payment timed out
 * - "partial"     - Underpaid
 * - "refund_pending" - Refund requested
 * - "refunded"    - Refund completed
 */
export const getPaymentStatus = async (paymentId) => {
    try {
        if (!paymentId) {
            return {
                success: false,
                error: 'Payment ID is required'
            };
        }

        const response = await axios.get(
            `${PAYMENT_API_BASE}/${paymentId}/status`,
            { headers: getAuthHeaders() }
        );

        return {
            success: true,
            data: response.data
        };
    } catch (error) {
        console.error('[paymentApi] Failed to get payment status:', error);
        return {
            success: false,
            error: error.response?.data?.error || error.message || 'Failed to get payment status'
        };
    }
};

// ═══════════════════════════════════════════════════════════════════════════
// GET /history - Payment History (JWT Required)
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Get user's payment history
 *
 * @param {number} page - Page number (default 1)
 * @param {number} limit - Items per page (default 10, max 50)
 * @returns {Promise<{success: boolean, data?: PaymentHistoryResponse, error?: string}>}
 *
 * Response format:
 * {
 *   payments: [
 *     { id, status, price_amount, price_currency, tokens_credited, created_at, paid_at }
 *   ],
 *   total: 1,
 *   page: 1,
 *   limit: 10
 * }
 */
export const getPaymentHistory = async (page = 1, limit = 10) => {
    try {
        const response = await axios.get(
            `${PAYMENT_API_BASE}/history`,
            {
                params: { page, limit: Math.min(limit, 50) },
                headers: getAuthHeaders()
            }
        );

        return {
            success: true,
            data: response.data
        };
    } catch (error) {
        console.error('[paymentApi] Failed to get payment history:', error);
        return {
            success: false,
            error: error.response?.data?.error || error.message || 'Failed to load payment history'
        };
    }
};

// ═══════════════════════════════════════════════════════════════════════════
// HELPER - Payment Status Utilities
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Check if payment status is a final state (no more polling needed)
 * @param {string} status - Payment status
 * @returns {boolean}
 */
export const isFinalStatus = (status) => {
    const finalStatuses = ['accepted', 'rejected', 'expired', 'refunded'];
    return finalStatuses.includes(status?.toLowerCase());
};

/**
 * Check if payment was successful
 * @param {string} status - Payment status
 * @returns {boolean}
 */
export const isSuccessStatus = (status) => {
    return status?.toLowerCase() === 'accepted';
};

/**
 * Get status display info (label and color)
 * @param {string} status - Payment status
 * @returns {{label: string, color: string, bgColor: string}}
 */
export const getStatusDisplayInfo = (status) => {
    const statusMap = {
        pending: {
            label: 'Pending',
            color: '#FBBF24',
            bgColor: 'rgba(251, 191, 36, 0.1)'
        },
        processing: {
            label: 'Processing',
            color: '#3B82F6',
            bgColor: 'rgba(59, 130, 246, 0.1)'
        },
        accepted: {
            label: 'Completed',
            color: '#27B56D',
            bgColor: 'rgba(39, 181, 109, 0.1)'
        },
        rejected: {
            label: 'Failed',
            color: '#EF4444',
            bgColor: 'rgba(239, 68, 68, 0.1)'
        },
        expired: {
            label: 'Expired',
            color: '#71717A',
            bgColor: 'rgba(113, 113, 122, 0.1)'
        },
        partial: {
            label: 'Partial Payment',
            color: '#F59E0B',
            bgColor: 'rgba(245, 158, 11, 0.1)'
        },
        refund_pending: {
            label: 'Refund Pending',
            color: '#F59E0B',
            bgColor: 'rgba(245, 158, 11, 0.1)'
        },
        refunded: {
            label: 'Refunded',
            color: '#71717A',
            bgColor: 'rgba(113, 113, 122, 0.1)'
        }
    };

    const normalizedStatus = status?.toLowerCase() || 'pending';
    return statusMap[normalizedStatus] || statusMap.pending;
};

// ═══════════════════════════════════════════════════════════════════════════
// HELPER - LocalStorage for pending payment tracking
// ═══════════════════════════════════════════════════════════════════════════

const PENDING_PAYMENT_KEY = 'qai_pending_payment';

/**
 * Store pending payment info before redirect
 * @param {number} paymentId - Payment ID
 * @param {number} tokens - Expected tokens
 */
export const storePendingPayment = (paymentId, tokens) => {
    localStorage.setItem(PENDING_PAYMENT_KEY, JSON.stringify({
        payment_id: paymentId,
        tokens: tokens,
        created_at: new Date().toISOString()
    }));
};

/**
 * Get pending payment info (if any)
 * @returns {{payment_id: number, tokens: number, created_at: string} | null}
 */
export const getPendingPayment = () => {
    try {
        const data = localStorage.getItem(PENDING_PAYMENT_KEY);
        return data ? JSON.parse(data) : null;
    } catch {
        return null;
    }
};

/**
 * Clear pending payment info
 */
export const clearPendingPayment = () => {
    localStorage.removeItem(PENDING_PAYMENT_KEY);
};
